import FormItem from '../ui/FormItem';
import Input from '../ui/Input';
import Button from '../ui/Buttons/Button';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiUpdateProfile } from '../../services/UserService';
import { setUser } from '../../store/auth/userSlice';

const validationSchema = Yup.object().shape({
  fullname: Yup.string()
    .min(2, 'Name must be at least 2 characters.')
    .required('Please enter your full name.'),
  email: Yup.string()
    .email('Please enter a valid email address.')
    .matches(
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      'Please enter a valid email address.'
    )
    .required('Please enter your email.'),
});

const CompleteForm = ({ onSkip, onComplete }) => {
  const dispatch = useDispatch();
  const params = useParams();
  const user = useSelector((state) => state.auth.user);
  const isOnProcess = params.storeSlug;
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleComplete = async (values) => {
    setIsFormSubmitting(true);
    setErrorMessage('');
    let payload = {
      fullname: values.fullname,
      email: values.email,
      phone: user.phone,
    };

    try {
      const success = await apiUpdateProfile(payload);
      if (success) {
        dispatch(
          setUser({
            fullname: success.data.data.fullname,
            phone: success.data.data.phone,
            email: success.data.data.email,
            id: success.data.data.id,
            favoritestores: success.data.data.favoritestores,
          })
        );
        setIsFormSubmitting(false);
        onComplete();
      } else {
        setErrorMessage('Failed to update profile. Please try again.');
        setIsFormSubmitting(false);
      }
    } catch (error) {
      let message = 'An error occurred. Please try again.';
      if (error?.response?.data) {
        // API sometimes returns string, sometimes object
        message =
          typeof error.response.data === 'string'
            ? error.response.data
            : error.response.data.message ||
              error.response.data.error ||
              message;
      }
      setErrorMessage(message);
      setIsFormSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <Formik
        initialValues={{
          fullname: user.fullname || '',
          email: user.email || '',
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          handleComplete(values);
        }}
      >
        {({ touched, errors, isValid, dirty }) => {
          return (
            <Form>
              <FormItem
                label="Full Name"
                invalid={errors.fullname && touched.fullname}
                errorMessage={errors.fullname}
              >
                <Field
                  className="text-center placeholder:text-center"
                  type="text"
                  autoComplete="off"
                  name="fullname"
                  placeholder="John"
                  component={Input}
                />
              </FormItem>
              <FormItem
                label="Email"
                invalid={errors.email && touched.email}
                errorMessage={errors.email}
              >
                <Field
                  className="text-center placeholder:text-center"
                  type="email"
                  autoComplete="off"
                  name="email"
                  placeholder="acme@acme.com"
                  component={Input}
                />
              </FormItem>
              {errorMessage && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600 text-center">
                    {errorMessage}
                  </p>
                </div>
              )}
              <FormItem className="mt-10">
                <div className="flex items-center gap-2">
                  {!isOnProcess && (
                    <Button
                      disabled={isFormSubmitting}
                      className="px-5 text-[13px] h-12"
                      variant="plain"
                      type="button"
                      onClick={() => onSkip()}
                    >
                      Skip
                    </Button>
                  )}
                  <Button
                    loading={isFormSubmitting}
                    className="w-full px-4 h-12"
                    variant="secondary"
                    type="submit"
                    disabled={!isValid || !dirty}
                  >
                    Done
                  </Button>
                </div>
              </FormItem>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default CompleteForm;
